import { createContext, useContext } from 'react';
import type { Position } from '../types';

/**
 * Discriminated union that captures every legal editor state. Illegal
 * combinations (e.g. placement active *and* markers selected) are now
 * impossible by construction.
 */
export type EditorMode =
  | { kind: 'idle' }
  | { kind: 'single-select'; uid: string }
  | { kind: 'multi-select'; uids: ReadonlySet<string> }
  | { kind: 'placement-awaiting-click' }
  | { kind: 'placement-awaiting-details'; coords: [number, number] };

export const IDLE_MODE: EditorMode = { kind: 'idle' };

/** Shared empty set so callers don't allocate on every read. */
export const EMPTY_UIDS: ReadonlySet<string> = new Set();

/** Derive the set of selected position uids from the current mode. */
export function getSelectedUids(mode: EditorMode): ReadonlySet<string> {
  if (mode.kind === 'single-select') return new Set([mode.uid]);
  if (mode.kind === 'multi-select') return mode.uids;
  return EMPTY_UIDS;
}

/** Build the tightest mode that represents the given uid set. */
export function modeFromUids(uids: ReadonlySet<string>): EditorMode {
  if (uids.size === 0) return IDLE_MODE;
  if (uids.size === 1) return { kind: 'single-select', uid: uids.values().next().value! };
  return { kind: 'multi-select', uids };
}

export interface EditorModeContextValue {
  isEditing: boolean;
  /** Current editor interaction mode — selection state, placement phase, or idle. */
  mode: EditorMode;
  positions: Position[];
  dirtyPositionUids: Set<string>;
  deletedPositionIds: Set<string>;
  selectPosition: (uid: string) => void;
  toggleInSelection: (uid: string) => void;
  /** Add each uid to the selection (union, no toggle). Used by lasso. */
  addToSelection: (uids: Iterable<string>) => void;
  clearSelection: () => void;
  beginPlacement: () => void;
  setPlacementCoords: (coords: [number, number]) => void;
  cancelPlacement: () => void;
  commitPlacement: (fields: Omit<Position, 'coordinates' | 'uid'>) => void;
  movePosition: (uid: string, coordinates: [number, number]) => void;
  /**
   * Move the dragged anchor to `newAnchorCoords` and shift all other
   * positions in the current selection by the same delta. Falls back to a
   * plain single-position move when the anchor is not selected or the
   * selection contains only one marker.
   */
  movePositionsGroup: (anchorUid: string, newAnchorCoords: [number, number]) => void;
  updatePosition: (uid: string, patch: Partial<Pick<Position, 'type' | 'labelPosition' | 'name' | 'description'>>) => void;
  renamePosition: (uid: string, newId: string) => void;
  deletePosition: (uid: string) => void;
  /**
   * Snap selected positions onto type-aware lanes along the best-fit line
   * through the selection.
   */
  alignSelection: () => void;
  /** Evenly space selected positions along the best-fit line, per-type. */
  distributeSelection: () => void;
  /** True when there is at least one entry on the undo stack. */
  canUndo: boolean;
  /** Pop the top of the undo stack and restore those coordinates. */
  undo: () => void;
  exportCSV: () => string;
}

export const EditorModeContext = createContext<EditorModeContextValue | null>(null);

export function useEditorModeContext(): EditorModeContextValue {
  const ctx = useContext(EditorModeContext);
  if (!ctx) throw new Error('useEditorModeContext must be used within EditorModeProvider');
  return ctx;
}
